import { useState, useEffect } from 'react';
import arTranslations from '../lang/ar.json';
import enTranslations from '../lang/en.json';

type Language = 'ar' | 'en';
type TranslationKey = string;

const translations = {
  ar: arTranslations,
  en: enTranslations,
};

export const useTranslation = () => {
  // تحميل اللغة: أولاً من الرابط (?lang=ar أو ?lang=en) ثم من localStorage (لدعم SEO والروابط البديلة)
  const getInitialLanguage = (): Language => {
    if (typeof window === 'undefined') return 'ar';
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang')?.toLowerCase();
    if (langParam === 'ar' || langParam === 'en') {
      localStorage.setItem('language', langParam);
      return langParam;
    }
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'ar' || savedLanguage === 'en')) {
      return savedLanguage;
    }
    return 'ar';
  };

  const [currentLanguage, setCurrentLanguage] = useState<Language>(getInitialLanguage());

  // حفظ اللغة في localStorage عند تغييرها
  const changeLanguage = (language: Language) => {
    setCurrentLanguage(language);
    localStorage.setItem('language', language);
    
    // تحديث اتجاه الصفحة
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';

    // إشعار بقية المكونات لتحديث ترجمتها فورًا
    window.dispatchEvent(new CustomEvent('language:changed', { detail: language }));
  };

  // الاستماع لتغييرات اللغة القادمة من مكونات أخرى
  useEffect(() => {
    const onLanguageChanged = (e: Event) => {
      // @ts-ignore
      const lang: Language = e.detail;
      setCurrentLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    };
    window.addEventListener('language:changed', onLanguageChanged);
    return () => window.removeEventListener('language:changed', onLanguageChanged);
  }, []);

  // دالة الترجمة
  const t = (key: TranslationKey, params?: any): string => {
    try {
      const keys = key.split('.');
      const langData = translations[currentLanguage];
  
      if (!langData) return key;
  
      let value: any = langData;
  
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return key; // fallback
        }
      }
  
      // simple replace params like {name}, {count} later if needed
      if (typeof value === 'string' && params) {
        Object.keys(params).forEach(paramKey => {
          const regex = new RegExp(`{${paramKey}}`, 'g');
          value = value.replace(regex, params[paramKey]);
        });
      }
  
      return typeof value === 'string' ? value : key;
    } catch {
      return key;
    }
  };
  

  return {
    t,
    currentLanguage,
    changeLanguage,
    isRTL: currentLanguage === 'ar',
  };
};
